/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {Devices, GenericObject, SCIPendingSettings} from '@ion/core/src/types/entities';
import {deepCopy} from '@ion/core/src/utils/ObjectUtil';
import Chip from '@ion/dls/src/components/chip/Chip';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import styles from '@ion/dls/src/editors/editorStyles';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';
import {
  getUpdatedValuesForChipDisplay,
  getValueLabel,
  MAX_DEVICE_TO_DISPLAY,
  path
} from './SCISettingsUtils';

// defines the styles used by SCI final view component ...
const styleOverrides = (theme: DLSTheme) => ({
  sciInfoText : {
    padding  : '25px 0 15px 0',
    fontSize : '16px'
  },
  chipWrapper : {
    padding   : '6px 0px 5px 0px',
    height    : '210px',
    overflowY : 'auto'
  },
  deviceWrapper : {
    padding   : '6px 0px 5px 0px',
    height    : '100px',
    overflowY : 'auto'
  },
  sciInfoContent : {
    padding   : '20px 38px 0 71px',
    height    : '90%',
    overflowY : 'auto'
  }
});

const sectionStyle = {
  paddingTop : '50px'
};

export interface BulkSCIFinalViewProps {
  selectedSCITypesDevices: Devices[];
  changedSettingFields: SCIPendingSettings;
  isAllRowsSelected: boolean;
  accountName: string;
  selectedTypeIndex: number;
  uploadValidation: boolean;
  setChangedSettingFields: Function;
  selectedDevicetype: string;
  setSelectedSCITypesDevices: Function;
  setTaskNickName: Function;
}

/**
 * Display Updated SCI setting
 */
export const BulkSCIFinalView = ({
  selectedSCITypesDevices,
  changedSettingFields,
  isAllRowsSelected,
  accountName,
  uploadValidation,
  selectedTypeIndex,
  setChangedSettingFields,
  selectedDevicetype,
  setTaskNickName,
  setSelectedSCITypesDevices
}: BulkSCIFinalViewProps) => {
  const editorStyles                      = useExtendStyles(styles, styleOverrides);
  const classes                           = editorStyles();
  const [changedFields, setChangedFields] = useState([]);
  const [taskName, setTaskName]           = useState<string>();
  const [showSnackBar, setShowSnackBar]   = useState<boolean>(false);
  const [errorMessage, setErrorMessage]   = useState<string>('');

  const getChangedFieldsLabelByLength = useMemo(() => {
    if (Object.keys(changedSettingFields).length === 1) {
      const feild = Object.entries(changedSettingFields)[0];
      return `${feild[0]} ${getValueLabel(feild[0], feild[1])}`;
    } else {
      return `${Object.keys(changedSettingFields).length} Field(s)`;
    }
  }, [changedSettingFields]);

  const getGPSChangedFieldsLabelByLength = useMemo(() => {
    if (Object.keys(changedSettingFields).length === 1) {
      const field = Object.entries(changedSettingFields)[0];
      return field[1] === true ? 'disable GPS' : 'enable GPS';
    }
  }, [changedSettingFields]);

  const getLabelByUploadType = useMemo(() => (
    (uploadValidation
      ? translate(`${path}.bulkInfoMessages.deviceUploadMessage`).concat(getChangedFieldsLabelByLength)
      : translate(`${path}.bulkInfoMessages.deviceUpdateMessage`).concat(getChangedFieldsLabelByLength))
  ), [uploadValidation, getChangedFieldsLabelByLength]);

  const getGPSLabelByUploadType = useMemo(() => (
    translate(`${path}.bulkInfoMessages.singleDeviceUpdateMessage`).concat(getGPSChangedFieldsLabelByLength)
  ), [getChangedFieldsLabelByLength]);

  useEffect(() => {
    if (!isEmpty(changedSettingFields)) {
      setChangedFields(getUpdatedValuesForChipDisplay(changedSettingFields, selectedDevicetype));
      let taskName = '';
      if (isAllRowsSelected) {
        taskName = translate(`${path}.bulkInfoMessages.allDevicesMessage`).concat(
          ` ${accountName} to update ${getChangedFieldsLabelByLength}`);
      } else {
        const selectedDevices = selectedSCITypesDevices[selectedTypeIndex];
        taskName = (selectedDevices.length === 1)
          ? Object.entries(changedSettingFields)[0][0] === 'disableGPS' ? `${getGPSLabelByUploadType}` : `${selectedDevices[0].name} ${getLabelByUploadType}`
          : `${selectedDevices.length} ${selectedDevicetype} ${getLabelByUploadType}`;
      }
      setTaskName(taskName);
      setTaskNickName(taskName);
    }
  }, [changedSettingFields,
    selectedDevicetype,
    selectedSCITypesDevices,
    isAllRowsSelected,
    accountName,
    selectedTypeIndex,
    getChangedFieldsLabelByLength]);

  const selectedSCTypeRemove = useCallback((id: number, allSCTypesDevices: Devices[]) => {
    if (selectedSCITypesDevices[selectedTypeIndex]?.flat()?.length === 1) {
      setShowSnackBar(true);
      setErrorMessage(translate(`${path}.bulkInfoMessages.removeDeviceMessage`));
      return;
    }
    const allDevice = [...allSCTypesDevices];
    allSCTypesDevices[selectedTypeIndex].forEach((device, i) => {
      if (device.id === id) {
        allDevice[selectedTypeIndex].splice(i, 1);
      }
    });
    setSelectedSCITypesDevices(allDevice);
  }, [selectedSCITypesDevices]);

  const removeChangedField = useCallback(({key}: GenericObject) => {
    const allChangedFeilds = Object.keys(changedSettingFields);
    if (allChangedFeilds.includes(key)) {
      if (allChangedFeilds.length === 1) {
        setShowSnackBar(true);
        setErrorMessage(translate(`${path}.bulkInfoMessages.removeSettingMessage`));
        return;
      }
      const allChangedSettingFields = deepCopy(changedSettingFields);
      delete allChangedSettingFields[key];
      setChangedSettingFields(allChangedSettingFields);
    }
  }, [changedSettingFields]);

  function closeSnack() {
    setShowSnackBar(false);
  }

  return (
    <div className={classes.sciInfoContent}>
      <div className={classes.sciInfoText}>
        {translate(`${path}.bulkInfoMessages.title`)}
      </div>
      <div>
        {selectedSCITypesDevices[selectedTypeIndex]?.length
          ? selectedSCITypesDevices[selectedTypeIndex].map((device, index) => (
            <Chip label={device?.name}
              size='small'
              maxWidth={false}
              borderRadius='56px'
              labelColor={dlsColor('grey2')}
              hoverDefault
              showCustomDeleteIcon
              noTextTransform
              isTopSpacing
              customDeleteIcon={<MaterialExitCloseIcon size={IconSize.small} />}
              onDelete={function() {
                selectedSCTypeRemove(device?.id, selectedSCITypesDevices);
              }}/>
          )) : ''}
      </div>
      <div className={classes.sciInfoText} style={sectionStyle}>
        {translate('admin-devices:services.manageServices.taskName.label')}
      </div>
      <FormItem disableMinWidth customWidth={10}>
        <FormTextField required
          disabled={false}
          placeholder={translate('admin-devices:services.manageServices.taskName.placeholder')}
          name='taskName'
          onChange={function(event) {
            setTaskName(event?.target?.value);
            setTaskNickName(event?.target?.value);
          }}
          hasError={false}
          errorText={translate('common:editors.shared.required')}
          value={taskName || ''} />
      </FormItem>
      <div className={classes.sciInfoText} style={sectionStyle}>
        {translate(`${path}.bulkInfoMessages.updatedFieldsLabel`)}
      </div>
      <div>
        {changedFields.length ? changedFields.map((field, index) => (
          <Chip label={field.label}
            size='small'
            maxWidth={false}
            borderRadius='56px'
            labelColor={dlsColor('grey2')}
            hoverDefault
            showCustomDeleteIcon
            noTextTransform
            isTopSpacing
            customDeleteIcon={<MaterialExitCloseIcon size={IconSize.small} />}
            onDelete={function() {
              removeChangedField(field);
            }}/>
        )) : ''}
      </div>
      <IonSnackbar show={showSnackBar}
        message={errorMessage}
        actionLabel={translate('common:editors.delete.ok')}
        onAction={closeSnack}
        onClose={closeSnack}
        autoHideDuration={3000} />
    </div>
  );
};

export default BulkSCIFinalView;
